import React, { useEffect, useState, useCallback } from 'react';
import './Referenskort.css';

/**
 * Referenskort
 *
 * Popup som visas när man klickar på ett portfolio-kort.
 * Zoomar upp från den punkt (originPoint) man klickade på.
 *
 * Props:
 * - project: {
 *     title, des, img,
 *     years?: (string|number)[]       // årtal, t.ex. [2019, 2020, 2023]
 *     delivered: string[]      // t.ex. ["Ljud", "Ljus", "Rigg", "Tekniker"]
 *     gallery: string[]        // bilder till bildgalleriet, inkl. huvudbilden om du vill
 *     credit?: string          // t.ex. "Stina Stjernkvist (SVT)"
 *   }
 * - onClose: () => void
 * - originPoint: { x: number, y: number } | null   // klickpositionen i viewport-koordinater
 */

// Formaterar en lista med årtal snyggt:
// - ett år: "2013"
// - flera år i följd: "2019–2021"
// - flera år som inte är i följd: "2013, 2019, 2023"
// - blandning av löpande och lösa år: "2013, 2019–2021"
export function formatYears(years) {
  const sorted = [...years].map(Number).sort((a, b) => a - b);
  const groups = [];
  let start = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];
    if (current === prev + 1) {
      prev = current;
      continue;
    }
    groups.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = current;
    prev = current;
  }

  return groups.join(', ');
}

function Referenskort({ project, onClose, originPoint }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const gallery =
    project?.gallery && project.gallery.length > 0
      ? project.gallery
      : project?.img
      ? [project.img]
      : [];

  const yearsList =
    project?.years && project.years.length > 0 ? project.years.map(String) : [];

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + gallery.length) % gallery.length));
  }, [gallery.length]);

  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % gallery.length));
  }, [gallery.length]);

  // Lås scroll på body medan modalen är öppen
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Tangentbord: Esc stänger (lightbox först om öppen), pilar bläddrar i lightbox
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        if (lightboxIndex !== null) {
          closeLightbox();
        } else {
          onClose();
        }
      } else if (lightboxIndex !== null) {
        if (e.key === 'ArrowLeft') showPrev();
        if (e.key === 'ArrowRight') showNext();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, onClose, closeLightbox, showPrev, showNext]);

  if (!project) return null;

  const transformOrigin = originPoint
    ? `${originPoint.x}px ${originPoint.y}px`
    : 'center center';

  const delivered =
    project.delivered && project.delivered.length > 0
      ? project.delivered
      : (project.topic || '').split(',').map((t) => t.trim()).filter(Boolean);

  return (
    <div className="pm-backdrop" onClick={onClose}>
      <div
        className="pm-panel"
        style={{ transformOrigin }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="pm-close" onClick={onClose} aria-label="Stäng">
          ✕
        </button>

        <div className="pm-hero">
          <img src={project.img} alt={project.title} />
          <div className="pm-hero-fade" />
        </div>

        <div className="pm-body">
          <div className="pm-heading-row">
            <h2 className="pm-title">{project.title}</h2>
            {yearsList.length > 0 && (
              <div className="pm-years-grid">
                {yearsList.map((y, i) => (
                  <span className="pm-year-chip" key={i}>
                    {y}
                  </span>
                ))}
              </div>
            )}
          </div>

          {delivered.length > 0 && (
            <div className="pm-tags">
              {delivered.map((tag, i) => (
                <span className="pm-tag" key={i}>
                  {tag}
                </span>
              ))}
            </div>
          )}

          {project.des && (
            <section className="pm-section">
              <h3 className="pm-section-title">Om projektet</h3>
              <p className="pm-section-text">{project.des}</p>
            </section>
          )}

          {gallery.length > 0 && (
            <section className="pm-section">
              {project.credit && (
                <p className="pm-credit">📷 Bilder av: {project.credit}</p>
              )}
              <h3 className="pm-section-title">Bildgalleri</h3>
              <div className="pm-gallery-grid">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    className="pm-gallery-thumb"
                    onClick={() => setLightboxIndex(i)}
                    aria-label={`Öppna bild ${i + 1}`}
                  >
                    <img src={src} alt={`${project.title} ${i + 1}`} loading="lazy" />
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {lightboxIndex !== null && (
        <div
          className="pm-lightbox"
          onClick={(e) => {
            e.stopPropagation();
            closeLightbox();
          }}
        >
          <button className="pm-lightbox-close" onClick={closeLightbox} aria-label="Stäng bildvisning">
            ✕
          </button>

          <button
            className="pm-lightbox-nav pm-lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              showPrev();
            }}
            aria-label="Föregående bild"
          >
            ‹
          </button>

          <img
            className="pm-lightbox-image"
            src={gallery[lightboxIndex]}
            alt={`${project.title} ${lightboxIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
          />

          <button
            className="pm-lightbox-nav pm-lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            aria-label="Nästa bild"
          >
            ›
          </button>

          <div className="pm-lightbox-counter">
            {lightboxIndex + 1} / {gallery.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default Referenskort;
